import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { buildApiUrl } from '../lib/api';
import './AdminPedidos.css';

function formatMoney(value, currency = 'ARS') {
  if (value === null || value === undefined || Number.isNaN(Number(value))) {
    return '-';
  }
  return `${Number(value).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currency || ''}`.trim();
}

function formatDate(value) {
  if (!value) {
    return '-';
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '-' : date.toLocaleString('es-AR');
}

function formatNumber(value, decimals = 2) {
  if (value === null || value === undefined || Number.isNaN(Number(value))) {
    return '-';
  }
  return Number(value).toLocaleString('es-AR', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

function normalizeStatusToken(value) {
  return String(value ?? '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

function humanizeStatus(value) {
  const token = normalizeStatusToken(value);
  return token ? token.replace(/-/g, ' ') : '-';
}

function getChipVariant(value) {
  const token = normalizeStatusToken(value);
  if (!token) {
    return 'neutral';
  }

  if (token === 'paid' || token === 'procesado' || token === 'despachado' || token === 'notificar-envio' || token === 'shipped' || token === 'delivered' || token === 'entregado') {
    return 'success';
  }

  if (token === 'en-transito' || token === 'salio-para-entrega') {
    return 'info';
  }

  if (token === 'fallido' || token === 'entrega-fallida') {
    return 'danger';
  }

  if (
    token === 'pending' ||
    token === 'pendiente' ||
    token === 'unpacked' ||
    token === 'unshipped' ||
    token === 'pendiente-de-colecta' ||
    token === 'pendiente-de-presentacion-en-deposito' ||
    token === 'recibido-en-deposito' ||
    token === 'en-preparacion' ||
    token === 'pendiente-de-entrega' ||
    token === 'recogido'
  ) {
    return 'warning';
  }

  if (token === 'partially-packed' || token === 'partially-fulfilled' || token === 'authorized' || token === 'pago-rechazado') {
    return 'info';
  }

  return 'neutral';
}

function getPaymentStatusLabel(value) {
  const token = normalizeStatusToken(value);
  if (!token) {
    return '-';
  }

  if (token === 'paid') {
    return 'Pagado';
  }

  if (token === 'pending') {
    return 'Pendiente';
  }

  if (token === 'authorized') {
    return 'Autorizado';
  }

  if (token === 'refunded') {
    return 'Reintegrado';
  }

  return humanizeStatus(value);
}

function getShippingStatusLabel(value) {
  const token = normalizeStatusToken(value);
  if (!token) {
    return 'Sin estado';
  }

  const labels = {
    importado: 'Importado',
    'importado-de-tiendanube': 'Importado de Tiendanube',
    pagado: 'Pagado',
    'pago-rechazado': 'Pago rechazado',
    'pendiente-de-colecta': 'Pendiente de colecta',
    'pendiente-de-presentacion-en-deposito': 'Pendiente de presentacion en deposito',
    'recibido-en-deposito': 'Recibido en deposito',
    'en-preparacion': 'En preparacion',
    'notificar-envio': 'Envío notificado',
    'pendiente-de-entrega': 'Pendiente de entrega',
    recogido: 'Recogido',
    despachado: 'Despachado',
    'en-transito': 'En tránsito',
    'salio-para-entrega': 'Salió para entrega',
    entregado: 'Entregado',
    fallido: 'Fallido',
    'entrega-fallida': 'Entrega fallida',
    unpacked: 'Sin preparar',
    unshipped: 'No enviado',
    shipped: 'Enviado',
    delivered: 'Entregado',
    'partially-packed': 'Parcialmente preparado',
    'partially-fulfilled': 'Parcialmente enviado',
  };

  return labels[token] || humanizeStatus(value);
}

function getInternalStatusLabel(value) {
  const token = normalizeStatusToken(value);
  if (!token) {
    return 'Pendiente';
  }

  const labels = {
    importado: 'Importado',
    'importado-de-tiendanube': 'Importado de Tiendanube',
    pagado: 'Pagado',
    'pago-rechazado': 'Pago rechazado',
    'pendiente-de-colecta': 'Pendiente de colecta',
    'pendiente-de-presentacion-en-deposito': 'Pendiente de presentacion en deposito',
    'recibido-en-deposito': 'Recibido en deposito',
    'en-preparacion': 'En preparacion',
    'notificar-envio': 'Envío notificado',
    'pendiente-de-entrega': 'Pendiente de entrega',
    recogido: 'Recogido',
    despachado: 'Despachado',
    'en-transito': 'En tránsito',
    'salio-para-entrega': 'Salió para entrega',
    entregado: 'Entregado',
    fallido: 'Fallido',
    'entrega-fallida': 'Entrega fallida',
    pendiente: 'Pendiente',
    procesado: 'Procesado',
    cancelado: 'Cancelado',
  };

  if (labels[token]) {
    return labels[token];
  }

  if (token === 'pendiente') {
    return 'Pendiente';
  }

  if (token === 'procesado') {
    return 'Procesado';
  }

  return humanizeStatus(value);
}

function getCobroStatusLabel(value) {
  const token = normalizeStatusToken(value);
  if (!token || token === 'pendiente') {
    return 'Pendiente';
  }

  if (token === 'pendiente-tarjeta') {
    return 'Sin tarjeta';
  }

  if (token === 'aprobado') {
    return 'Aprobado';
  }

  if (token === 'rechazado' || token === 'error') {
    return 'Rechazado';
  }

  return humanizeStatus(value);
}

function getCobroChipVariant(value) {
  const token = normalizeStatusToken(value);
  if (!token || token === 'pendiente' || token === 'pendiente-tarjeta') {
    return 'warning';
  }

  if (token === 'aprobado') {
    return 'success';
  }

  if (token === 'rechazado' || token === 'error') {
    return 'danger';
  }

  return 'neutral';
}

function buildShippingAddressLabel(address) {
  if (!address) {
    return '-';
  }

  const line1 = [address.address, address.number].filter(Boolean).join(' ').trim();
  const line2 = [address.floor ? `Piso ${address.floor}` : null, address.locality, address.city, address.province]
    .filter(Boolean)
    .join(', ');

  const primary = line1 || line2;
  if (!primary) {
    return '-';
  }

  return line2 && line2 !== primary ? `${primary} | ${line2}` : primary;
}

const ICONS = {
  search: (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/></svg>
  ),
  refresh: (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8"/><path d="M21 3v5h-5"/><path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16"/><path d="M8 16H3v5"/></svg>
  ),
  eye: (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z"/><circle cx="12" cy="12" r="3"/></svg>
  ),
  package: (
    <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="m7.5 4.27 9 5.15"/><path d="M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z"/><path d="m3.3 7 8.7 5 8.7-5"/><path d="M12 22V12"/></svg>
  ),
  mapPin: (
    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z"/><circle cx="12" cy="10" r="3"/></svg>
  ),
  chevronDown: (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m6 9 6 6 6-6"/></svg>
  ),
  chevronUp: (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m18 15-6-6-6 6"/></svg>
  ),
  truck: (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 18V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v11a1 1 0 0 0 1 1h2"/><path d="M15 18H9"/><path d="M19 18h2a1 1 0 0 0 1-1v-3.65a1 1 0 0 0-.22-.624l-2.48-3.1A1 1 0 0 0 18.52 9H15"/><circle cx="17" cy="18" r="2"/><circle cx="7" cy="18" r="2"/></svg>
  ),
};

const ESTADOS_FILTRO = [
  { value: 'PENDIENTE', label: 'Pendientes' },
  { value: 'PROCESADO', label: 'Procesados' },
  { value: 'CANCELADO', label: 'Cancelados' },
  { value: '', label: 'Todos' },
];

export default function AdminPedidos() {
  const { authRequestJson, logout } = useAuth();

  const [status, setStatus] = useState(null);
  const [storeId, setStoreId] = useState('');
  const [estadoFiltro, setEstadoFiltro] = useState('PENDIENTE');
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [q, setQ] = useState('');
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [processingId, setProcessingId] = useState(null);
  const [cancellingId, setCancellingId] = useState(null);
  const [markingPaidId, setMarkingPaidId] = useState(null);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [detailCache, setDetailCache] = useState({});
  const [trackingNumber, setTrackingNumber] = useState('');
  const [shippingStatus, setShippingStatus] = useState('');
  const [sendFulfillmentEmail, setSendFulfillmentEmail] = useState(false);
  const [savingTracking, setSavingTracking] = useState(false);
  const [savingStatus, setSavingStatus] = useState(false);

  const installedStores = useMemo(
    () => status?.installedStores?.filter((s) => s.activo) || [],
    [status]
  );

  const loadStatus = useCallback(async () => {
    try {
      const statusData = await authRequestJson('/admin/tiendanube/status?take=1000');
      setStatus(statusData);
    } catch (err) {
      if (err?.status === 401) {
        logout();
      }
    }
  }, [authRequestJson, logout]);

  useEffect(() => {
    void loadStatus();
  }, [loadStatus]);

  useEffect(() => {
    if (installedStores.length > 0 && !storeId) {
      setStoreId(String(installedStores[0].tiendaNubeId));
    }
  }, [installedStores, storeId]);

  const loadPedidos = useCallback(async () => {
    if (!storeId) {
      setData([]);
      setPagination(null);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams({
        storeId,
        page: String(page),
        pageSize: String(pageSize),
      });
      if (estadoFiltro) {
        params.set('estado', estadoFiltro);
      }
      if (q.trim()) {
        params.set('q', q.trim());
      }

      const response = await authRequestJson(`/admin/tiendanube/pedidos?${params.toString()}`);
      setData(response.data || []);
      setPagination(response.pagination || null);
    } catch (err) {
      if (err?.status === 401) {
        logout();
        setError('La sesion vencio. Volve a iniciar sesion.');
      } else {
        setError(err?.message || 'No se pudieron cargar los pedidos.');
      }
    } finally {
      setLoading(false);
    }
  }, [authRequestJson, logout, storeId, estadoFiltro, page, pageSize, q]);

  useEffect(() => {
    setPage(1);
  }, [storeId, estadoFiltro, q]);

  useEffect(() => {
    void loadPedidos();
  }, [loadPedidos]);

  const handleSync = async () => {
    if (!storeId) {
      setError('Selecciona una tienda.');
      return;
    }

    setSyncing(true);
    setError(null);
    setMessage(null);

    try {
      const response = await authRequestJson(`/admin/tiendanube/pedidos/sync?storeId=${encodeURIComponent(storeId)}`, {
        method: 'POST',
      });
      setMessage(
        `Sincronizacion completa: ${response.sincronizados} pedido(s) (${response.creados} creados, ${response.actualizados} actualizados, ${response.errores} errores).`
      );
      await loadPedidos();
    } catch (err) {
      if (err?.status === 401) {
        logout();
        setError('La sesion vencio. Volve a iniciar sesion.');
      } else {
        setError(err?.message || 'No se pudieron sincronizar los pedidos.');
      }
    } finally {
      setSyncing(false);
    }
  };

  const handleProcesar = async (id) => {
    setProcessingId(id);
    setError(null);
    setMessage(null);

    try {
      await authRequestJson(`/admin/tiendanube/pedidos/${id}/procesar`, {
        method: 'POST',
      });
      setMessage(`Pedido #${id} marcado como procesado.`);
      await loadPedidos();
    } catch (err) {
      if (err?.status === 401) {
        logout();
        setError('La sesion vencio. Volve a iniciar sesion.');
      } else {
        setError(err?.message || 'No se pudo procesar el pedido.');
      }
    } finally {
      setProcessingId(null);
    }
  };

  const handleCancelar = async (id) => {
    setCancellingId(id);
    setError(null);
    setMessage(null);

    try {
      await authRequestJson(`/admin/tiendanube/pedidos/${id}/cancelar`, {
        method: 'POST',
      });
      setMessage(`Pedido #${id} cancelado.`);
      await loadPedidos();
    } catch (err) {
      if (err?.status === 401) {
        logout();
        setError('La sesion vencio. Volve a iniciar sesion.');
      } else {
        setError(err?.message || 'No se pudo cancelar el pedido.');
      }
    } finally {
      setCancellingId(null);
    }
  };

  const handleMarcarPagado = async (id) => {
    setMarkingPaidId(id);
    setError(null);
    setMessage(null);

    try {
      const response = await authRequestJson(`/admin/tiendanube/pedidos/${id}/marcar-pagado`, {
        method: 'POST',
      });
      setMessage(`Pago confirmado y tracking ${response.trackingNumber} generado para el pedido #${id}.`);
      await loadPedidos();
      if (expandedId === id) {
        const detailResponse = await authRequestJson(`/admin/tiendanube/pedidos/${id}`);
        setDetailCache((prev) => ({ ...prev, [id]: detailResponse.data }));
      }
    } catch (err) {
      if (err?.status === 401) {
        logout();
        setError('La sesion vencio. Volve a iniciar sesion.');
      } else {
        setError(err?.message || 'No se pudo confirmar el pago del pedido.');
      }
    } finally {
      setMarkingPaidId(null);
    }
  };

  const handleExport = async () => {
    if (!storeId) {
      setError('Selecciona una tienda.');
      return;
    }

    setError(null);
    setMessage(null);

    try {
      const params = new URLSearchParams({ storeId });
      if (estadoFiltro) {
        params.set('estado', estadoFiltro);
      }
      if (q.trim()) {
        params.set('q', q.trim());
      }

      const response = await fetch(`${buildApiUrl('/admin/tiendanube/pedidos/export')}?${params.toString()}`, {
        headers: {
          Accept: 'text/csv',
          Authorization: `Bearer ${localStorage.getItem('token') || ''}`,
        },
      });

      if (response.status === 401) {
        logout();
        setError('La sesion vencio. Volve a iniciar sesion.');
        return;
      }

      if (!response.ok) {
        const text = await response.text();
        throw new Error(text || 'No se pudo exportar el archivo.');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `pedidos-tiendanube-${storeId}-${(estadoFiltro || 'todos').toLowerCase()}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      setMessage('Exportacion generada correctamente.');
    } catch (err) {
      setError(err?.message || 'No se pudo exportar el archivo.');
    }
  };

  const toggleDetail = async (id) => {
    if (expandedId === id) {
      setExpandedId(null);
      return;
    }

    setExpandedId(id);
    setTrackingNumber('');
    setShippingStatus('');
    setSendFulfillmentEmail(false);

    if (!detailCache[id]) {
      try {
        const response = await authRequestJson(`/admin/tiendanube/pedidos/${id}`);
        setDetailCache((prev) => ({ ...prev, [id]: response.data }));
      } catch (err) {
        if (err?.status === 401) {
          logout();
        }
      }
    }
  };

  const handleReportTracking = async (id) => {
    if (!trackingNumber.trim()) {
      setError('Ingresa un tracking number.');
      return;
    }

    setSavingTracking(true);
    setError(null);
    setMessage(null);

    try {
      await authRequestJson(`/admin/tiendanube/pedidos/${id}/tracking`, {
        method: 'POST',
        body: JSON.stringify({ trackingNumber: trackingNumber.trim() }),
      });
      setMessage(`Tracking guardado para el pedido #${id}.`);
      const response = await authRequestJson(`/admin/tiendanube/pedidos/${id}`);
      setDetailCache((prev) => ({ ...prev, [id]: response.data }));
      await loadPedidos();
    } catch (err) {
      if (err?.status === 401) {
        logout();
        setError('La sesion vencio. Volve a iniciar sesion.');
      } else {
        setError(err?.message || 'No se pudo guardar el tracking.');
      }
    } finally {
      setSavingTracking(false);
    }
  };

  const handleUpdateShippingStatus = async (id) => {
    if (!shippingStatus) {
      setError('Selecciona un estado de envio.');
      return;
    }

    setSavingStatus(true);
    setError(null);
    setMessage(null);

    try {
      await authRequestJson(`/admin/tiendanube/pedidos/${id}/shipping-status`, {
        method: 'POST',
        body: JSON.stringify({
          shippingStatus,
          sendFulfillmentEmail,
        }),
      });
      setMessage(`Estado de envio actualizado para el pedido #${id}.`);
      const response = await authRequestJson(`/admin/tiendanube/pedidos/${id}`);
      setDetailCache((prev) => ({ ...prev, [id]: response.data }));
      await loadPedidos();
    } catch (err) {
      if (err?.status === 401) {
        logout();
        setError('La sesion vencio. Volve a iniciar sesion.');
      } else {
        setError(err?.message || 'No se pudo actualizar el estado de envio.');
      }
    } finally {
      setSavingStatus(false);
    }
  };

  const handleEmitLabel = (pedido) => {
    if (!pedido?.tiendaNubeOrderId || !storeId) {
      setError('Falta informacion para emitir el rotulo.');
      return;
    }

    const url = new URL(buildApiUrl('/tiendanube/shipping/labels'), window.location.href);
    url.searchParams.set('store', storeId);
    url.searchParams.append('id', pedido.tiendaNubeOrderId);
    window.open(url.toString(), '_blank', 'noopener,noreferrer');
  };

  const totalPages = pagination?.totalPages || 0;
  const totalItems = pagination?.total || 0;
  const startItem = totalItems === 0 ? 0 : (page - 1) * pageSize + 1;
  const endItem = totalItems === 0 ? 0 : Math.min(page * pageSize, totalItems);

  return (
    <div className="pedidos-page animate-fade-in">
      <div className="pedidos-card">
        <div className="pedidos-header">
          <div>
            <p className="eyebrow">Tiendanube</p>
            <h1 className="pedidos-title">Pedidos pagados</h1>
            <p className="pedidos-subtitle">Sincroniza y revisa los pedidos pagados pendientes de procesar en las tiendas instaladas.</p>
          </div>
          <div className="pedidos-actions">
            <Link to="/admin/tiendanube" className="pedidos-btn pedidos-btn-secondary">
              Volver
            </Link>
            <button
              type="button"
              className="pedidos-btn pedidos-btn-secondary"
              onClick={() => void loadPedidos()}
              disabled={loading}
            >
              <span className={loading ? 'animate-spin' : ''}>{ICONS.refresh}</span>
              Actualizar
            </button>
          </div>
        </div>

        <div className="pedidos-filters">
          <div className="pedidos-field" style={{ flex: '0 1 260px' }}>
            <label>Tienda</label>
            <select value={storeId} onChange={(e) => setStoreId(e.target.value)} disabled={installedStores.length === 0}>
              <option value="">{installedStores.length === 0 ? 'Sin tiendas activas' : 'Selecciona una tienda'}</option>
              {installedStores.map((store) => (
                <option key={store.tiendaNubeId} value={store.tiendaNubeId}>
                  {store.tiendaNubeId}
                  {store.clienteNombre ? ` | ${store.clienteNombre}` : ''}
                </option>
              ))}
            </select>
          </div>

          <div className="pedidos-field" style={{ flex: '1 1 240px' }}>
            <label>Buscar</label>
            <div style={{ position: 'relative' }}>
              <span style={{ position: 'absolute', left: '0.6rem', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }}>
                {ICONS.search}
              </span>
              <input
                type="text"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Numero, cliente, localidad..."
                style={{ paddingLeft: '2rem' }}
              />
            </div>
          </div>

          <button
            type="button"
            className="pedidos-btn pedidos-btn-primary"
            onClick={handleSync}
            disabled={syncing || !storeId}
          >
            {syncing ? 'Sincronizando...' : 'Sincronizar pedidos'}
          </button>
          <button
            type="button"
            className="pedidos-btn pedidos-btn-secondary"
            onClick={() => void handleExport()}
            disabled={!storeId}
          >
            Exportar Excel
          </button>
        </div>

        <div className="pedidos-tabs" role="tablist" aria-label="Filtro de pedidos">
          {ESTADOS_FILTRO.map((estado) => (
            <button
              key={estado.value || 'todos'}
              type="button"
              className={`pedidos-tab ${estadoFiltro === estado.value ? 'active' : ''}`}
              onClick={() => setEstadoFiltro(estado.value)}
            >
              {estado.label}
            </button>
          ))}
        </div>

        {error ? <div className={`pedidos-alert pedidos-alert-error`}>{error}</div> : null}
        {message ? <div className={`pedidos-alert pedidos-alert-success`}>{message}</div> : null}

        <div className="pedidos-table-wrap">
          {data.length === 0 && !loading ? (
            <div className="pedidos-empty">
              {ICONS.package}
              <p>No hay pedidos para mostrar.</p>
            </div>
          ) : (
            <table className="pedidos-table">
              <thead>
                <tr>
                  <th>Pedido</th>
                  <th>Cliente</th>
                  <th>Localidad</th>
                  <th>Valor declarado</th>
                  <th>Valor de viaje</th>
                  <th>Peso / Bultos</th>
                  <th>Estado</th>
                  <th>Cobro</th>
                  <th style={{ textAlign: 'right' }}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {data.map((pedido) => {
                  const estadoInterno = String(pedido.estadoInterno || '').toUpperCase();
                  const esProcesado = estadoInterno === 'PROCESADO';
                  const esCancelado = estadoInterno === 'CANCELADO';
                  const actionLabel = esProcesado ? 'Cancelar' : esCancelado ? 'Reprocesar' : 'Procesar';
                  const actionHandler = esProcesado ? () => handleCancelar(pedido.id) : () => handleProcesar(pedido.id);
                  const actionDisabled = esProcesado ? cancellingId === pedido.id : processingId === pedido.id;

                  return (
                  <Fragment key={pedido.id}>
                    <tr>
                      <td>
                        <div className="pedidos-number">
                          #{pedido.tiendaNubeOrderNumber || pedido.tiendaNubeOrderId}
                        </div>
                        <div className="pedidos-meta">TN ID: {pedido.tiendaNubeOrderId}</div>
                        {pedido.trackingNumber ? (
                          <div className="pedidos-meta">Tracking: {pedido.trackingNumber}</div>
                        ) : null}
                      </td>
                      <td>
                        <div>{pedido.contactName || '-'}</div>
                        <div className="pedidos-meta">{pedido.contactEmail || '-'}</div>
                      </td>
                      <td>
                        <div>{pedido.localidadDestino || '-'}</div>
                        {pedido.codigoPostalDestino ? (
                          <div className="pedidos-localidad">
                            {ICONS.mapPin}
                            CP {pedido.codigoPostalDestino}
                          </div>
                        ) : null}
                      </td>
                      <td>{formatMoney(pedido.subtotal, pedido.currency)}</td>
                      <td>{formatMoney(pedido.valorViaje, pedido.currency)}</td>
                      <td>
                        <div>{formatNumber(pedido.pesoRealTotal, 3)} kg</div>
                        <div className="pedidos-meta">{pedido.bultosTotal || 0} bultos</div>
                      </td>
                      <td>
                        <span
                          className={`pedidos-badge ${
                            estadoInterno === 'PROCESADO'
                              ? 'pedidos-badge-procesado'
                              : estadoInterno === 'CANCELADO'
                                ? 'pedidos-badge-danger'
                              : 'pedidos-badge-pendiente'
                          }`}
                        >
                          {getInternalStatusLabel(pedido.estadoInterno)}
                        </span>
                      </td>
                      <td>
                        <span className={`pedidos-badge pedidos-badge-${getCobroChipVariant(pedido.cobroEstado)}`}>
                          {getCobroStatusLabel(pedido.cobroEstado)}
                        </span>
                        {pedido.cobroMonto > 0 ? (
                          <div className="pedidos-meta">{formatMoney(pedido.cobroMonto, pedido.currency)}</div>
                        ) : null}
                      </td>
                      <td>
                        <div className="pedidos-actions-cell">
                          <button
                            type="button"
                            title="Ver detalle"
                            className="pedidos-icon-btn"
                            onClick={() => toggleDetail(pedido.id)}
                          >
                            {expandedId === pedido.id ? ICONS.chevronUp : ICONS.chevronDown}
                          </button>
                          <button
                            type="button"
                            className={`pedidos-btn ${esProcesado ? 'pedidos-btn-secondary' : 'pedidos-btn-primary'}`}
                            onClick={actionHandler}
                            disabled={actionDisabled}
                            style={{ padding: '0.4rem 0.75rem', fontSize: '0.8rem' }}
                          >
                            {actionDisabled
                              ? esProcesado
                                ? 'Cancelando...'
                                : 'Procesando...'
                              : actionLabel}
                          </button>
                        </div>
                      </td>
                    </tr>
                    {expandedId === pedido.id && (
                      <tr>
                        <td colSpan="9" style={{ padding: 0 }}>
                          <PedidoDetailPanel
                            pedido={detailCache[pedido.id]}
                            loading={!detailCache[pedido.id]}
                            trackingNumber={trackingNumber}
                            onTrackingNumberChange={setTrackingNumber}
                            shippingStatus={shippingStatus}
                            onShippingStatusChange={setShippingStatus}
                            sendFulfillmentEmail={sendFulfillmentEmail}
                            onSendFulfillmentEmailChange={setSendFulfillmentEmail}
                            savingTracking={savingTracking}
                            savingStatus={savingStatus}
                            markingPaidId={markingPaidId}
                            onMarcarPagado={() => handleMarcarPagado(pedido.id)}
                            onReportTracking={() => handleReportTracking(pedido.id)}
                            onUpdateShippingStatus={() => handleUpdateShippingStatus(pedido.id)}
                            onEmitLabel={() => handleEmitLabel(pedido)}
                          />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {pagination && totalPages > 1 && (
          <div className="pedidos-pagination">
            <div className="pedidos-pagination-info">
              Mostrando {startItem} - {endItem} de {totalItems} pedidos
            </div>
            <div className="pedidos-pagination-actions">
              <button
                type="button"
                className="pedidos-btn pedidos-btn-secondary"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                Anterior
              </button>
              <span style={{ fontSize: '0.85rem', color: '#475569' }}>
                Pagina {page} de {totalPages}
              </span>
              <button
                type="button"
                className="pedidos-btn pedidos-btn-secondary"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              >
                Siguiente
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function PedidoDetailPanel({
  pedido,
  loading,
  trackingNumber,
  onTrackingNumberChange,
  shippingStatus,
  onShippingStatusChange,
  sendFulfillmentEmail,
  onSendFulfillmentEmailChange,
  savingTracking,
  savingStatus,
  markingPaidId,
  onMarcarPagado,
  onReportTracking,
  onUpdateShippingStatus,
  onEmitLabel,
}) {
  if (loading) {
    return (
      <div className="pedidos-detail-panel">
        <div className="pedidos-detail-state">
          {ICONS.package}
          <p>Cargando detalle...</p>
        </div>
      </div>
    );
  }

  if (!pedido) {
    return (
      <div className="pedidos-detail-panel">
        <div className="pedidos-detail-state">
          {ICONS.package}
          <p>No se pudo cargar el detalle.</p>
        </div>
      </div>
    );
  }

  const products = pedido.products || [];
  const orderNumber = pedido.tiendaNubeOrderNumber || pedido.tiendaNubeOrderId;
  const customerMeta = [pedido.contactEmail, pedido.contactPhone].filter(Boolean).join(' | ') || 'Sin datos de contacto';
  const internalStatus = getInternalStatusLabel(pedido.estadoInterno);
  const paymentStatus = getPaymentStatusLabel(pedido.paymentStatus);
  const shippingStatusLabel = getShippingStatusLabel(pedido.estadoInterno || pedido.shippingStatus);
  const shippingRoute = [pedido.localidadDestino, pedido.codigoPostalDestino ? `CP ${pedido.codigoPostalDestino}` : null]
    .filter(Boolean)
    .join(' | ') || '-';
  const shippingAddress = buildShippingAddressLabel(pedido.shippingAddress);

  return (
    <div className="pedidos-detail-panel">
      <div className="pedidos-detail-hero">
        <div className="pedidos-detail-hero-main">
          <p className="pedidos-detail-kicker">Detalle del pedido</p>
          <div className="pedidos-detail-title-row">
            <div>
              <h4 className="pedidos-detail-title">Pedido #{orderNumber}</h4>
              <p className="pedidos-detail-subtitle">
                {pedido.contactName || 'Sin nombre'} | {customerMeta}
              </p>
            </div>
            <div className="pedidos-detail-chips">
              <span className={`pedidos-chip pedidos-chip-${getChipVariant(pedido.estadoInterno)}`}>
                Estado interno: {internalStatus}
              </span>
              <span className="pedidos-chip pedidos-chip-success">Pago: {paymentStatus}</span>
              <span className={`pedidos-chip pedidos-chip-${getChipVariant(pedido.estadoInterno || pedido.shippingStatus)}`}>
                Envio: {shippingStatusLabel}
              </span>
            </div>
          </div>
        </div>
        <div className="pedidos-detail-hero-side">
          <div className="pedidos-detail-mini-stat">
            <span>TN ID</span>
            <strong>{pedido.tiendaNubeOrderId}</strong>
          </div>
          <div className="pedidos-detail-mini-stat">
            <span>Procesado</span>
            <strong>{formatDate(pedido.procesadoEn)}</strong>
          </div>
        </div>
      </div>

      <div className="pedidos-detail-summary-grid">
        <div className="pedidos-detail-card pedidos-detail-card-highlight">
          <span>Valor declarado</span>
          <strong>{formatMoney(pedido.subtotal, pedido.currency)}</strong>
          <small>Subtotal informado por Tiendanube</small>
        </div>
        <div className="pedidos-detail-card">
          <span>Valor de viaje</span>
          <strong>{formatMoney(pedido.valorViaje, pedido.currency)}</strong>
          <small>Tarifa calculada para este pedido</small>
        </div>
        <div className="pedidos-detail-card">
          <span>Peso / bultos</span>
          <strong>{formatNumber(pedido.pesoRealTotal, 3)} kg</strong>
          <small>{pedido.bultosTotal || 0} bultos totales</small>
        </div>
        <div className="pedidos-detail-card">
          <span>Localidad destino</span>
          <strong>{pedido.localidadDestino || '-'}</strong>
          <small>{shippingRoute}</small>
        </div>
      </div>

      <div className="pedidos-detail-columns">
        <section className="pedidos-detail-section">
          <div className="pedidos-detail-section-head">
            <div>
              <p className="pedidos-detail-kicker">Informacion operativa</p>
              <h4 className="pedidos-section-title">Datos del pedido</h4>
            </div>
          </div>
          <div className="pedidos-detail-grid pedidos-detail-grid--compact">
            <div className="pedidos-detail-card">
              <span>Cliente</span>
              <strong>{pedido.contactName || '-'}</strong>
              <small>{customerMeta}</small>
            </div>
            <div className="pedidos-detail-card">
              <span>Direccion</span>
              <strong>{shippingAddress}</strong>
              <small>{shippingRoute}</small>
            </div>
            <div className="pedidos-detail-card">
              <span>Identificacion</span>
              <strong>{pedido.contactIdentification || '-'}</strong>
              <small>Dato fiscal o DNI del comprador</small>
            </div>
            <div className="pedidos-detail-card">
              <span>Estado Tiendanube</span>
              <strong>{pedido.status || '-'}</strong>
              <small>Estado original de la orden</small>
            </div>
            <div className="pedidos-detail-card">
              <span>Creado</span>
              <strong>{formatDate(pedido.creadoEn)}</strong>
              <small>Alta en la base local</small>
            </div>
            <div className="pedidos-detail-card">
              <span>Actualizado</span>
              <strong>{formatDate(pedido.actualizadoEn)}</strong>
              <small>Ultimo cambio sincronizado</small>
            </div>
            <div className="pedidos-detail-card">
              <span>Tracking actual</span>
              <strong>{pedido.trackingNumber || '-'}</strong>
              <small>Numero operativo para reportar a Tiendanube</small>
            </div>
            <div className="pedidos-detail-card">
              <span>Referencia de envio</span>
              <strong>{pedido.shippingOptionReference || '-'}</strong>
              <small>Opcion usada por la orden</small>
            </div>
          </div>
        </section>

        <section className="pedidos-detail-section pedidos-detail-section--actions">
          <div className="pedidos-detail-section-head">
            <div>
              <p className="pedidos-detail-kicker">Acciones</p>
              <h4 className="pedidos-section-title">Gestion de envio</h4>
            </div>
          </div>
          <div className="pedidos-action-stack">
            <div className="pedidos-product-card pedidos-product-card--control">
              <strong>Tracking number</strong>
              <p className="pedidos-detail-section-copy">
                Carga el numero de seguimiento para mantener Tiendanube y el panel sincronizados.
              </p>
              <div className="pedidos-form-grid pedidos-form-grid--detail">
                <label>
                  Nuevo tracking
                  <input
                    type="text"
                    value={trackingNumber}
                    onChange={(e) => onTrackingNumberChange(e.target.value)}
                    placeholder="Ej: ABC123456"
                  />
                </label>
              </div>
              <button
                type="button"
                className="pedidos-btn pedidos-btn-primary"
                onClick={onReportTracking}
                disabled={savingTracking}
              >
                {savingTracking ? 'Guardando...' : 'Guardar tracking'}
              </button>
            </div>

            <div className="pedidos-product-card pedidos-product-card--control">
              <strong>Estado de envio</strong>
              <p className="pedidos-detail-section-copy">
                Actualiza el estado logistico y replica los eventos de tracking en todos los fulfillments de la orden.
              </p>
              <div className="pedidos-form-grid pedidos-form-grid--detail">
                <label>
                  Estado logistico
                  <select value={shippingStatus} onChange={(e) => onShippingStatusChange(e.target.value)}>
                    <option value="">Selecciona un estado</option>
                    <option value="pendiente_de_colecta">Pendiente de colecta</option>
                    <option value="pendiente_de_presentacion_en_deposito">Pendiente de presentacion en deposito</option>
                    <option value="recibido_en_deposito">Recibido en deposito</option>
                    <option value="en_preparacion">En preparación</option>
                    <option value="notificar_envio">Notificar envío</option>
                    <option value="pendiente_de_entrega">Pendiente de entrega</option>
                    <option value="recogido">Recogido</option>
                    <option value="despachado">Despachado</option>
                    <option value="en_transito">En tránsito</option>
                    <option value="salio_para_entrega">Salió para entrega</option>
                    <option value="entregado">Entregado</option>
                    <option value="entrega_fallida">Entrega fallida</option>
                  </select>
                </label>
              </div>
              <label className="pedidos-checkbox pedidos-checkbox--detail">
                <input
                  type="checkbox"
                  checked={sendFulfillmentEmail}
                  onChange={(e) => onSendFulfillmentEmailChange(e.target.checked)}
                />
                Enviar email de fulfillment al cliente
              </label>
              <button
                type="button"
                className="pedidos-btn pedidos-btn-primary"
                onClick={onUpdateShippingStatus}
                disabled={savingStatus}
              >
                {savingStatus ? 'Actualizando...' : 'Actualizar estado'}
              </button>
              <button
                type="button"
                className="pedidos-btn pedidos-btn-secondary"
                onClick={onEmitLabel}
              >
                Emitir rótulo
              </button>
            </div>

            <div className="pedidos-product-card pedidos-product-card--control">
              <strong>Confirmación de pago</strong>
              <p className="pedidos-detail-section-copy">
                Estado: <span className={`pedidos-badge pedidos-badge-${getCobroChipVariant(pedido?.cobroEstado)}`}>
                  {getCobroStatusLabel(pedido?.cobroEstado)}
                </span>
              </p>
              {pedido?.cobroMpPaymentId ? (
                <p className="pedidos-detail-section-copy">
                  Referencia: <strong>{pedido.cobroMpPaymentId}</strong>
                  {pedido.cobroMonto ? ` (${formatMoney(pedido.cobroMonto, pedido.currency)})` : ''}
                </p>
              ) : null}
              {pedido?.cobroError ? (
                <p className="pedidos-detail-section-copy" style={{ color: '#b91c1c' }}>
                  {pedido.cobroError}
                </p>
              ) : null}
              <p className="pedidos-detail-section-copy">
                Confirma el pago logístico desde el administrador. Se genera y guarda el tracking operativo sin solicitar datos de tarjeta.
              </p>
              <button
                type="button"
                className="pedidos-btn pedidos-btn-primary"
                onClick={onMarcarPagado}
                disabled={markingPaidId === pedido?.id}
              >
                {markingPaidId === pedido?.id ? 'Confirmando...' : 'Marcar pagado y generar tracking'}
              </button>
            </div>
          </div>
        </section>
      </div>

      <section className="pedidos-detail-section">
        <div className="pedidos-detail-section-head">
          <div>
            <p className="pedidos-detail-kicker">Contenido</p>
            <h4 className="pedidos-section-title">Productos</h4>
          </div>
        </div>
        <div className="pedidos-products pedidos-products--detail">
          {products.length === 0 ? (
            <div className="pedidos-detail-state pedidos-detail-state-inline">
              {ICONS.package}
              <p>Sin productos.</p>
            </div>
          ) : (
            products.map((product) => (
              <div key={product.id} className="pedidos-product-card pedidos-product-card--detail">
                <strong>{product.name || 'Sin nombre'}</strong>
                <div className="pedidos-product-meta">
                  <span>SKU: {product.sku || 'SIN SKU'}</span>
                  <span>Cantidad: {product.quantity || 0}</span>
                  <span>Peso: {formatNumber(product.weight, 3)} kg</span>
                  <span>Alto: {formatNumber(product.height, 2)} cm</span>
                  <span>Ancho: {formatNumber(product.width, 2)} cm</span>
                  <span>Largo: {formatNumber(product.depth, 2)} cm</span>
                </div>
              </div>
            ))
          )}
        </div>
      </section>
    </div>
  );
}
